/**
 * Direct HTTP wrappers for the admin auth endpoints.
 *
 *   POST /admin/auth/login
 *   POST /admin/auth/refresh
 *   GET  /admin/auth/me
 *   POST /admin/auth/logout
 *
 * Used by `AdminAuthProvider`. Lives next to admin-real-client so the
 * underlying axios instance + interceptors are shared, but kept as a
 * separate module because:
 *   - it's NOT part of the read-only contract barrel (auth has its own
 *     lifecycle on the frontend),
 *   - it talks to /auth/* directly without going through the mock/real
 *     mode switch.
 *
 * Real backend; in mock mode the AdminAuthProvider takes a different
 * path entirely and never touches this module.
 */
import { adminHttp } from "@/lib/api/admin-http-client";
import { ApiError } from "@/lib/api/api-error";

export interface AdminLoginRequest {
  email: string;
  password: string;
}

export interface AdminLoginResponseProfile {
  id: string;
  email: string;
  display_name: string;
  role: "owner" | "ops_admin" | "support" | "viewer";
  permissions: string[];
  mfa_enabled: boolean;
}

export interface AdminLoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  admin: AdminLoginResponseProfile;
}

/**
 * POST /admin/auth/login when the admin has MFA enabled (step 1 of 2).
 *
 * Backend returns HTTP 200 with this body INSTEAD of tokens
 * (`AdminMfaRequiredResponse`): no session is issued yet. The caller must
 * complete /admin/auth/mfa/verify with `challenge_id` + a TOTP/recovery code.
 */
export interface AdminMfaChallengeResponse {
  mfa_required: true;
  challenge_id: string;
  expires_in: number;
}

/** /admin/auth/login returns either a full session OR an MFA challenge (both 200). */
export type AdminLoginResult = AdminLoginResponse | AdminMfaChallengeResponse;

/** Narrow a login result to the MFA-challenge branch. */
export function isAdminMfaChallengeResponse(
  result: AdminLoginResult,
): result is AdminMfaChallengeResponse {
  return (
    (result as AdminMfaChallengeResponse).mfa_required === true &&
    typeof (result as AdminMfaChallengeResponse).challenge_id === "string"
  );
}

export interface AdminMeResponse {
  admin: AdminLoginResponseProfile;
}

export async function postAdminLogin(
  payload: AdminLoginRequest,
): Promise<AdminLoginResult> {
  const resp = await adminHttp.post<AdminLoginResult>(
    "/admin/auth/login",
    payload,
  );
  return resp.data;
}

export interface AdminMfaVerifyRequest {
  challenge_id: string;
  /** TOTP or recovery code. Never logged. */
  code: string;
}

/**
 * POST /admin/auth/mfa/verify — step 2 of admin login.
 *
 * On success the backend mints the same `AdminLoginResponse` a non-MFA login
 * would return. A 401 (`mfa_failed`) means the code OR the challenge was
 * invalid or expired. `/admin/auth/mfa/verify` is treated as an auth endpoint
 * by the HTTP interceptor, so that 401 surfaces directly (no silent refresh).
 */
export async function postAdminMfaVerify(
  payload: AdminMfaVerifyRequest,
): Promise<AdminLoginResponse> {
  const resp = await adminHttp.post<AdminLoginResponse>(
    "/admin/auth/mfa/verify",
    payload,
  );
  return resp.data;
}

export interface AdminRefreshRequest {
  refresh_token: string;
}

export type AdminRefreshResponse = AdminLoginResponse;

export async function postAdminRefresh(
  payload: AdminRefreshRequest,
): Promise<AdminRefreshResponse> {
  const resp = await adminHttp.post<AdminRefreshResponse>(
    "/admin/auth/refresh",
    payload,
  );
  return resp.data;
}

export async function getAdminMe(accessToken: string): Promise<AdminMeResponse> {
  const resp = await adminHttp.get<AdminMeResponse>("/admin/auth/me", {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return resp.data;
}

export async function postAdminLogout(
  accessToken: string,
  refreshToken: string,
): Promise<void> {
  await adminHttp.post(
    "/admin/auth/logout",
    { refresh_token: refreshToken },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
}

/**
 * Convenience: returns `true` if the error is the documented "MFA required but
 * no TOTP secret enrolled" 501 from /admin/auth/login (backend code
 * `mfa_not_enrolled`). This is the edge case where an admin is flagged for MFA
 * but has never completed enrolment — they cannot self-serve; an owner must
 * enrol or clear MFA for them. Distinct from the normal MFA challenge, which is
 * a 200 `AdminMfaChallengeResponse`, not an error.
 */
export function isAdminMfaNotEnrolledError(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 501 &&
    typeof err.details === "object" &&
    err.details !== null &&
    "code" in err.details &&
    (err.details as { code?: string }).code === "mfa_not_enrolled"
  );
}
